// Botón de export a CSV para montar en la toolbar de una lista.
// Recibe la grid API de AG Grid (capturala con onGridReady en la lista) y el nombre del módulo.
import React, { useState } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { FaFileCsv } from 'react-icons/fa';
import CustomSnackbar from './CustomSnackbar';
import { downloadCsv } from '../utils/csvExport';

function ExportCsvButton({ gridApi, moduleName = 'tabla', size = 'sm' }) {
  const [show, setShow] = useState(false);
  const [scope, setScope] = useState('filtered');
  const [counts, setCounts] = useState({ filtered: 0, all: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });

  const open = () => {
    if (!gridApi) return;
    let all = 0;
    gridApi.forEachNode(() => (all += 1));
    if (all === 0) {
      setSnackbar({
        open: true,
        type: 'warning',
        message: 'La tabla no tiene filas para exportar.',
      });
      return;
    }
    const filtered = gridApi.getDisplayedRowCount();
    setCounts({ filtered, all });
    setScope(filtered === all ? 'all' : 'filtered');
    setShow(true);
  };

  const doExport = () => {
    if (!gridApi) return;
    const displayed = gridApi.getDisplayedRowCount();
    if (scope === 'filtered' && displayed === 0) {
      setShow(false);
      setSnackbar({
        open: true,
        type: 'warning',
        message: 'No hay filas para exportar con los filtros actuales.',
      });
      return;
    }
    try {
      const columnKeys = (gridApi.getColumns() || [])
        .filter((c) => {
          const h = c.getColDef().headerName;
          // Excluir la columna de acciones y las columnas sin encabezado (íconos/links).
          return h && h !== 'Opciones';
        })
        .map((c) => c.getColId());
      const csv = gridApi.getDataAsCsv({
        columnKeys,
        exportedRows: scope === 'all' ? 'all' : 'filteredAndSorted',
      });
      setShow(false);
      if (csv == null) {
        setSnackbar({
          open: true,
          type: 'error',
          message: 'No pudimos generar el archivo. Probá de nuevo.',
        });
        return;
      }
      const stamp = new Date().toISOString().slice(0, 10);
      const fileName = `${moduleName || 'tabla'}_${
        scope === 'all' ? 'todo' : 'filtrado'
      }_${stamp}.csv`;
      downloadCsv(csv, fileName);
      const k = scope === 'all' ? counts.all : displayed;
      setSnackbar({
        open: true,
        type: 'success',
        message: `Listo, se descargaron ${k} filas en CSV.`,
      });
    } catch (err) {
      setShow(false);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'No pudimos generar el archivo. Probá de nuevo.',
      });
    }
  };

  return (
    <>
      <Button
        variant="outline-secondary"
        size={size}
        className="d-flex align-items-center"
        onClick={open}
        disabled={!gridApi}
        title="Exportar la tabla a CSV"
      >
        <FaFileCsv className="me-1" /> Exportar
      </Button>

      <Modal show={show} onHide={() => setShow(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Exportar a CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {counts.filtered !== counts.all && (
            <Form.Check
              type="radio"
              name="export-scope"
              id="export-scope-filtered"
              className="mb-2"
              checked={scope === 'filtered'}
              onChange={() => setScope('filtered')}
              label={
                <span>
                  Solo lo que veo
                  <span className="text-muted small d-block">
                    Respeta los filtros que tenés aplicados ({counts.filtered} filas).
                  </span>
                </span>
              }
            />
          )}
          <Form.Check
            type="radio"
            name="export-scope"
            id="export-scope-all"
            checked={scope === 'all'}
            onChange={() => setScope('all')}
            label={
              <span>
                Todo
                <span className="text-muted small d-block">
                  {counts.filtered !== counts.all
                    ? `Exporta las ${counts.all} filas de la tabla, ignorando filtros.`
                    : `Exporta las ${counts.all} filas de la tabla.`}
                </span>
              </span>
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShow(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={doExport}>
            Exportar
          </Button>
        </Modal.Footer>
      </Modal>

      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </>
  );
}

export default ExportCsvButton;
